/** Admin (T1.09): species, members, teams and codes, zones, roster CSV import, key in a paper sheet. Admin or lead only. */
import {
  CONDITIONS,
  HEIGHT_METHODS,
  OBSERVATION_CHIPS,
  ROLES,
  SOIL_MOISTURE,
  SOIL_TEMP,
  SPECIES_TYPES,
  WEATHER_CHIPS,
  formatTeamCode,
  generateTeamCode,
  isValidTag,
  parseRoster,
  pointEwkt,
  polygonEwkt,
  validateMeasurement,
  type Condition,
  type GeoJsonPolygon,
  type HeightMethod,
  type ObservationChip,
  type SoilMoisture,
  type SoilTemp,
  type SpeciesRow,
  type TeamRow,
} from '@treetrack/protocol';
import { fmtDate, h, replace, todayIso, uuid } from '../dom';
import { loadMembers, loadTeams, type MemberWithName, type ProjectBundle } from '../data';
import { randomBytes, teamCodeHash } from '../hash';
import { L, speciesName, t } from '../i18n';
import { toHash } from '../router';
import { getSession } from '../session';
import { supabase, unwrap } from '../supabase';
import { button, card, chips, empty, errorBox, input, labelled, loading, select, table } from './shared';

export function adminView(b: ProjectBundle, reload: () => Promise<void>): HTMLElement {
  const root = h('div', { class: 'admin' });
  root.appendChild(h('a', { class: 'back', href: toHash({ name: 'project', id: b.project.id }) }, `← ${b.project.name}`));
  root.appendChild(h('h1', null, t('admin.title')));
  root.appendChild(paperSheetPanel(b, reload));
  root.appendChild(rosterImportPanel(b, reload));
  root.appendChild(teamsPanel(b));
  root.appendChild(zonesPanel(b, reload));
  root.appendChild(speciesPanel(b, reload));
  root.appendChild(membersPanel(b));
  return root;
}

function db() {
  if (!supabase) throw new Error('backend not configured');
  return supabase;
}

// ---------------------------------------------------------------------------
// Species
// ---------------------------------------------------------------------------
function speciesPanel(b: ProjectBundle, reload: () => Promise<void>): HTMLElement {
  const status = h('div');
  const list = table(
    ['ES', 'EN', L('Científico', 'Scientific'), L('Tipo', 'Type'), L('Nativa', 'Native')],
    b.species.map((s) => [s.name_es, s.name_en, h('i', null, s.scientific ?? ''), t('species_type.' + s.type), s.native == null ? '—' : s.native ? t('common.yes') : t('common.no')]),
  );
  const es = input({ placeholder: 'Nombre en español', required: true });
  const en = input({ placeholder: 'English name', required: true });
  const sci = input({ placeholder: 'Scientific name' });
  const type = select(SPECIES_TYPES.map((x) => ({ value: x, label: t('species_type.' + x) })), { value: 'other' });
  const native = select(
    [
      { value: '', label: '—' },
      { value: 'true', label: t('common.yes') },
      { value: 'false', label: t('common.no') },
    ],
    {},
  );
  const form = h(
    'form',
    {
      class: 'form form-row',
      onsubmit: async (e: Event) => {
        e.preventDefault();
        status.replaceChildren();
        try {
          unwrap(
            await db()
              .from('species')
              .insert({ name_es: es.value.trim(), name_en: en.value.trim(), scientific: sci.value.trim() || null, type: type.value, native: native.value === '' ? null : native.value === 'true' }),
          );
          await reload();
        } catch (err) {
          status.replaceChildren(errorBox(err));
        }
      },
    },
    labelled('ES', es),
    labelled('EN', en),
    labelled(L('Científico', 'Scientific'), sci),
    labelled(L('Tipo', 'Type'), type),
    labelled(L('Nativa', 'Native'), native),
    h('button', { type: 'submit', class: 'btn btn-primary' }, t('admin.add_species')),
  );
  return card(`${t('admin.species')} (${b.species.length})`, b.species.length ? list : empty(t('common.none')), h('details', null, h('summary', null, t('admin.add_species')), form, status));
}

// ---------------------------------------------------------------------------
// Members
// ---------------------------------------------------------------------------
function membersPanel(b: ProjectBundle): HTMLElement {
  const body = h('div', null, loading());
  const status = h('div');
  const email = input({ type: 'email', placeholder: 'teacher@school.org', required: true });
  const role = select(ROLES.filter((r) => r !== 'admin' && r !== 'field').map((r) => ({ value: r, label: t('role.' + r) })), { value: 'lead' });

  async function refresh() {
    try {
      const members = await loadMembers(b.project.id);
      body.replaceChildren(
        members.length
          ? table(
              [L('Nombre', 'Name'), L('Rol', 'Role'), ''],
              members.map((m: MemberWithName) => [
                m.display_name,
                t('role.' + m.role),
                m.user_id === getSession().user?.id
                  ? ''
                  : button(
                      t('admin.remove'),
                      async () => {
                        try {
                          unwrap(await db().from('project_members').delete().eq('project_id', b.project.id).eq('user_id', m.user_id));
                          await refresh();
                        } catch (err) {
                          status.replaceChildren(errorBox(err));
                        }
                      },
                      'btn btn-ghost btn-sm',
                    ),
              ]),
            )
          : empty(t('common.none')),
      );
    } catch (err) {
      body.replaceChildren(errorBox(err));
    }
  }
  void refresh();

  const form = h(
    'form',
    {
      class: 'form form-row',
      onsubmit: async (e: Event) => {
        e.preventDefault();
        status.replaceChildren();
        try {
          const userId = unwrap(await db().rpc('user_id_by_email', { p_email: email.value.trim() })) as string | null;
          if (!userId) throw new Error(L('Esa persona aún no ha iniciado sesión. Pídele que entre con el enlace por correo primero.', 'That person has not signed in yet. Ask them to sign in with the email link first.'));
          unwrap(await db().from('project_members').upsert({ project_id: b.project.id, user_id: userId, role: role.value }, { onConflict: 'project_id,user_id' }));
          email.value = '';
          await refresh();
        } catch (err) {
          status.replaceChildren(errorBox(err));
        }
      },
    },
    labelled(t('admin.invite_email'), email),
    labelled(L('Rol', 'Role'), role),
    h('button', { type: 'submit', class: 'btn btn-primary' }, t('admin.add')),
  );
  return card(t('admin.members'), body, form, status);
}

// ---------------------------------------------------------------------------
// Teams and codes
// ---------------------------------------------------------------------------
function teamsPanel(b: ProjectBundle): HTMLElement {
  const body = h('div', null, loading());
  const status = h('div');
  const name = input({ placeholder: 'Equipo Ceiba', required: true, maxlength: 40 });

  async function refresh() {
    try {
      const teams = await loadTeams(b.project.id);
      body.replaceChildren(
        teams.length
          ? table(
              [t('admin.team_name'), L('Activo', 'Active'), L('Desde', 'Since'), ''],
              teams.map((tm: TeamRow) => [
                tm.name,
                tm.active ? t('common.yes') : t('common.no'),
                fmtDate(tm.created_at),
                tm.active
                  ? button(
                      t('admin.deactivate'),
                      async () => {
                        try {
                          unwrap(await db().from('teams').update({ active: false }).eq('id', tm.id));
                          await refresh();
                        } catch (err) {
                          status.replaceChildren(errorBox(err));
                        }
                      },
                      'btn btn-ghost btn-sm',
                    )
                  : '',
              ]),
            )
          : empty(t('common.none')),
      );
    } catch (err) {
      body.replaceChildren(errorBox(err));
    }
  }
  void refresh();

  const form = h(
    'form',
    {
      class: 'form form-row',
      onsubmit: async (e: Event) => {
        e.preventDefault();
        status.replaceChildren();
        try {
          const code = generateTeamCode(randomBytes);
          const code_hash = await teamCodeHash(b.project.id, code);
          unwrap(await db().from('teams').insert({ project_id: b.project.id, name: name.value.trim(), code_hash, active: true }));
          status.replaceChildren(
            h(
              'div',
              { class: 'code-reveal' },
              h('div', { class: 'hint' }, `${t('auth.project_code')}: ${b.project.code}`),
              h('div', { class: 'code' }, formatTeamCode(code)),
              h('div', { class: 'warn' }, t('admin.code_shown_once')),
            ),
          );
          name.value = '';
          await refresh();
        } catch (err) {
          status.replaceChildren(errorBox(err));
        }
      },
    },
    labelled(t('admin.team_name'), name),
    h('button', { type: 'submit', class: 'btn btn-primary' }, t('admin.new_team')),
  );
  return card(t('admin.teams'), h('p', { class: 'hint' }, t('auth.privacy_note')), body, form, status);
}

// ---------------------------------------------------------------------------
// Zones
// ---------------------------------------------------------------------------
function zonesPanel(b: ProjectBundle, reload: () => Promise<void>): HTMLElement {
  const status = h('div');
  const label = input({ placeholder: 'A', required: true, maxlength: 12 });
  const rows = input({ type: 'number', min: 1, placeholder: L('filas', 'rows') });
  const cols = input({ type: 'number', min: 1, placeholder: L('columnas', 'cols') });
  const geom = h('textarea', { class: 'input', rows: 2, placeholder: '{"type":"Polygon","coordinates":[[[lng,lat],...]]}' });
  const form = h(
    'form',
    {
      class: 'form form-row',
      onsubmit: async (e: Event) => {
        e.preventDefault();
        status.replaceChildren();
        try {
          let ewkt: string | null = null;
          if (geom.value.trim()) {
            const poly = JSON.parse(geom.value) as GeoJsonPolygon;
            if (poly.type !== 'Polygon') throw new Error('zone must be a GeoJSON Polygon');
            ewkt = polygonEwkt(poly);
          }
          unwrap(
            await db()
              .from('zones')
              .insert({ project_id: b.project.id, label: label.value.trim().toUpperCase(), grid_rows: rows.value ? Number(rows.value) : null, grid_cols: cols.value ? Number(cols.value) : null, geom: ewkt }),
          );
          await reload();
        } catch (err) {
          status.replaceChildren(errorBox(err));
        }
      },
    },
    labelled(t('field.zone'), label),
    labelled(t('field.grid'), h('div', { class: 'row' }, rows, cols)),
    labelled(L('Polígono GeoJSON (opcional)', 'GeoJSON Polygon (optional)'), geom),
    h('button', { type: 'submit', class: 'btn btn-primary' }, t('admin.new_zone')),
  );
  const treeCount = new Map<string, number>();
  for (const tr of b.trees) if (tr.zone_id) treeCount.set(tr.zone_id, (treeCount.get(tr.zone_id) ?? 0) + 1);
  return card(
    `${t('admin.zones')} (${b.zones.length})`,
    b.zones.length ? table([t('field.zone'), t('field.grid'), t('nav.roster'), L('Contorno', 'Outline')], b.zones.map((z) => [z.label, z.grid_rows ? `${z.grid_rows} × ${z.grid_cols ?? '?'}` : '—', String(treeCount.get(z.id) ?? 0), z.geom_geojson ? t('common.yes') : t('common.no')])) : empty(t('common.none')),
    h('details', null, h('summary', null, t('admin.new_zone')), form, status),
  );
}

// ---------------------------------------------------------------------------
// Roster CSV import (T1.07)
// ---------------------------------------------------------------------------
function rosterImportPanel(b: ProjectBundle, reload: () => Promise<void>): HTMLElement {
  const file = input({ type: 'file', accept: '.csv,text/csv' });
  const preview = h('div');
  const status = h('div');
  file.addEventListener('change', async () => {
    const f = file.files?.[0];
    if (!f) return;
    const text = await f.text();
    const result = parseRoster(text, { projectCode: b.project.code, species: b.species, zones: b.zones });
    const existing = new Set(b.trees.map((tr) => tr.tag));
    const fresh = result.trees.filter((tr) => !existing.has(tr.tag));
    const dup = result.trees.length - fresh.length;
    replace(
      preview,
      h('p', null, t('roster.import_preview', { ok: fresh.length, issues: result.issues.length }), dup ? ` · ${dup} ${L('ya existen', 'already exist')}` : ''),
      result.issues.length ? table([L('Línea', 'Line'), t('field.tag'), L('Problema', 'Problem')], result.issues.map((i) => [String(i.line), i.tag, i.message])) : null,
      fresh.length
        ? button(
            t('roster.import_apply', { n: fresh.length }),
            async () => {
              status.replaceChildren(loading());
              try {
                const rows = fresh.map((tr) => {
                  const id = uuid();
                  return {
                    id,
                    client_id: id,
                    project_id: b.project.id,
                    zone_id: tr.zone_id,
                    tag: tr.tag,
                    species_id: tr.species_id,
                    geom: tr.lat != null && tr.lng != null ? pointEwkt(tr.lat, tr.lng) : null,
                    gps_accuracy_m: tr.gps_accuracy_m,
                    grid_row: tr.grid_row,
                    grid_col: tr.grid_col,
                    planted_on: tr.planted_on,
                    status: 'active',
                    created_by: getSession().user?.id ?? null,
                  };
                });
                for (let i = 0; i < rows.length; i += 200) unwrap(await db().from('trees').insert(rows.slice(i, i + 200)));
                status.replaceChildren();
                preview.replaceChildren();
                file.value = '';
                await reload();
              } catch (err) {
                status.replaceChildren(errorBox(err));
              }
            },
          )
        : null,
    );
  });
  return card(
    t('roster.import_csv'),
    h('p', { class: 'hint' }, L('Columnas: Tag, Species, Zone, Lat, Lng, Accuracy, Planted, Row, Col, Notes (los nombres de la hoja de la Fase 0 también sirven).', 'Columns: Tag, Species, Zone, Lat, Lng, Accuracy, Planted, Row, Col, Notes (the Phase 0 sheet headers also work).')),
    file,
    preview,
    status,
  );
}

// ---------------------------------------------------------------------------
// Key in a paper sheet (T1.09)
// ---------------------------------------------------------------------------
interface SheetRow {
  el: HTMLElement;
  read(): { tag: string; condition: Condition; heightCm: number | null; heightMethod: HeightMethod | null; g10: number | null; g130: number | null; soilMoisture: SoilMoisture | null; soilTemp: SoilTemp | null; chips: Set<string>; note: string };
  setIssues(msgs: string[], level: 'error' | 'warning' | 'none'): void;
}

function paperSheetPanel(b: ProjectBundle, reload: () => Promise<void>): HTMLElement {
  const tagList = h('datalist', { id: 'tag-list' }, ...b.trees.map((tr) => h('option', { value: tr.tag })));
  const date = input({ type: 'date', value: todayIso(), required: true });
  const zone = select([{ value: '', label: '—' }, ...b.zones.map((z) => ({ value: z.id, label: z.label }))], {});
  const team = input({ placeholder: 'Equipo Ceiba', maxlength: 60 });
  const members = input({ placeholder: 'JA, MR, LS', maxlength: 120 });
  const weather = new Set<string>();
  const care = h('textarea', { class: 'input', rows: 2 });
  const mulch = h('textarea', { class: 'input', rows: 2 });
  const pest = h('textarea', { class: 'input', rows: 2 });
  const bottles = input({ type: 'number', min: 0 });
  const notes = h('textarea', { class: 'input', rows: 2 });
  const rowsEl = h('div', { class: 'sheet-rows' });
  const status = h('div');
  const rows: SheetRow[] = [];

  const addRow = () => {
    const r = sheetRow();
    rows.push(r);
    rowsEl.appendChild(r.el);
  };
  for (let i = 0; i < 5; i++) addRow();

  async function submit() {
    status.replaceChildren();
    const tagToTree = new Map(b.trees.map((tr) => [tr.tag, tr]));
    const filled = rows.map((r) => ({ r, v: r.read() })).filter(({ v }) => v.tag);
    let blocked = false;
    const items: Array<{ tree_id: string; v: ReturnType<SheetRow['read']> }> = [];
    for (const { r, v } of filled) {
      const tree = tagToTree.get(v.tag);
      if (!tree) {
        r.setIssues([isValidTag(v.tag) ? t('roster.empty') : t('roster.tag_invalid')], 'error');
        blocked = true;
        continue;
      }
      const girths = [v.g10 != null ? { heightCm: 10, girthCm: v.g10 } : null, v.g130 != null ? { heightCm: 130, girthCm: v.g130 } : null].filter((g): g is { heightCm: number; girthCm: number } => g != null);
      const issues = validateMeasurement({
        condition: v.condition,
        heightCm: v.heightCm,
        heightMethod: v.heightMethod,
        girths,
        soilMoisture: v.soilMoisture,
        soilTemp: v.soilTemp,
        chips: [...v.chips] as ObservationChip[],
        note: v.note || null,
        hasPhoto: true, // a paper sheet never has the photo; the warning would be noise here
      });
      const errors = issues.filter((i) => i.level === 'error');
      r.setIssues(issues.map((i) => t('validation.' + i.code)), errors.length ? 'error' : issues.length ? 'warning' : 'none');
      if (errors.length) blocked = true;
      items.push({ tree_id: tree.id, v });
    }
    if (blocked || items.length === 0) return;
    status.replaceChildren(loading());
    try {
      const surveyId = uuid();
      unwrap(
        await db()
          .from('surveys')
          .insert({
            id: surveyId,
            client_id: surveyId,
            project_id: b.project.id,
            zone_id: zone.value || null,
            date: date.value,
            team_name: team.value.trim() || null,
            members: members.value.trim() || null,
            weather: [...weather],
            care_plan: care.value.trim() || null,
            mulch_plan: mulch.value.trim() || null,
            pest_notes: pest.value.trim() || null,
            irrigation_bottles: bottles.value ? Number(bottles.value) : null,
            notes: notes.value.trim() || null,
            created_by: getSession().user?.id ?? null,
            device_time: new Date().toISOString(),
          }),
      );
      const measurements = items.map(({ tree_id, v }) => {
        const id = uuid();
        return {
          id,
          client_id: id,
          tree_id,
          survey_id: surveyId,
          condition: v.condition,
          height_cm: v.condition === 'NF' ? null : v.heightCm,
          height_method: v.heightMethod,
          soil_moisture: v.soilMoisture,
          soil_temp: v.soilTemp,
          chips: [...v.chips],
          note: v.note || null,
          recorded_by: getSession().user?.id ?? null,
          device_time: new Date().toISOString(),
          _g10: v.g10,
          _g130: v.g130,
        };
      });
      unwrap(await db().from('measurements').insert(measurements.map(({ _g10, _g130, ...m }) => m)));
      const girths = measurements.flatMap((m) => [
        ...(m._g10 != null && m.condition !== 'NF' ? [{ measurement_id: m.id, height_cm: 10, girth_cm: m._g10 }] : []),
        ...(m._g130 != null && m.condition !== 'NF' ? [{ measurement_id: m.id, height_cm: 130, girth_cm: m._g130 }] : []),
      ]);
      if (girths.length) unwrap(await db().from('measurement_girths').insert(girths));
      status.replaceChildren(h('p', { class: 'ok' }, `${t('common.ok')}: ${measurements.length} ${t('survey.measurements').toLowerCase()}`));
      rows.length = 0;
      rowsEl.replaceChildren();
      for (let i = 0; i < 5; i++) addRow();
      await reload();
    } catch (err) {
      status.replaceChildren(errorBox(err));
    }
  }

  return card(
    t('admin.paper_sheet'),
    h('p', { class: 'hint' }, t('admin.paper_hint')),
    tagList,
    h(
      'div',
      { class: 'form form-row' },
      labelled(t('field.date'), date),
      labelled(t('field.zone'), zone),
      labelled(t('field.team'), team),
      labelled(t('field.members'), members),
      labelled(t('field.weather'), chips(WEATHER_CHIPS.map((w) => ({ value: w, label: t('weather.' + w) })), weather)),
    ),
    h('h3', null, t('survey.measurements')),
    rowsEl,
    button('+', addRow, 'btn btn-secondary btn-sm'),
    h('h3', null, t('survey.summary')),
    h('div', { class: 'form' }, labelled(t('survey.care_plan'), care), labelled(t('survey.mulch_plan'), mulch), labelled(t('survey.pest_notes'), pest), labelled(t('survey.irrigation_bottles'), bottles), labelled(t('field.note'), notes)),
    button(t('survey.finish'), () => void submit(), 'btn btn-primary btn-block'),
    status,
  );
}

function sheetRow(): SheetRow {
  const tag = input({ list: 'tag-list', placeholder: 'DS-A-017', class: 'input input-sm tag', style: { textTransform: 'uppercase' } });
  const condition = select(CONDITIONS.map((c) => ({ value: c, label: c })), { value: 'H', class: 'input input-sm' });
  const height = input({ type: 'number', min: 0, step: 1, placeholder: t('field.height_cm'), class: 'input input-sm' });
  const method = select([{ value: '', label: '—' }, ...HEIGHT_METHODS.map((m) => ({ value: m, label: t('height_method.' + m) }))], { class: 'input input-sm' });
  const g10 = input({ type: 'number', min: 0, step: 0.1, placeholder: t('field.girth_at', { height: 10 }), class: 'input input-sm' });
  const g130 = input({ type: 'number', min: 0, step: 0.1, placeholder: t('field.girth_at', { height: 130 }), class: 'input input-sm' });
  const moisture = select([{ value: '', label: '—' }, ...SOIL_MOISTURE.map((m) => ({ value: m, label: t('soil_moisture.' + m) }))], { class: 'input input-sm' });
  const temp = select([{ value: '', label: '—' }, ...SOIL_TEMP.map((m) => ({ value: m, label: t('soil_temp.' + m) }))], { class: 'input input-sm' });
  const chipSet = new Set<string>();
  const note = input({ placeholder: t('field.note'), class: 'input input-sm' });
  const issues = h('div', { class: 'row-issues' });
  const el = h(
    'div',
    { class: 'sheet-row' },
    h('div', { class: 'sheet-grid' }, tag, condition, height, method, g10, g130, moisture, temp, note),
    chips(OBSERVATION_CHIPS.map((c) => ({ value: c, label: t('chip.' + c) })), chipSet),
    issues,
  );
  const num = (i: HTMLInputElement) => (i.value === '' ? null : Number(i.value));
  return {
    el,
    read: () => ({
      tag: tag.value.trim().toUpperCase(),
      condition: condition.value as Condition,
      heightCm: num(height),
      heightMethod: (method.value || null) as HeightMethod | null,
      g10: num(g10),
      g130: num(g130),
      soilMoisture: (moisture.value || null) as SoilMoisture | null,
      soilTemp: (temp.value || null) as SoilTemp | null,
      chips: chipSet,
      note: note.value.trim(),
    }),
    setIssues: (msgs, level) => {
      issues.className = `row-issues ${level}`;
      issues.replaceChildren(...msgs.map((m) => h('span', null, m)));
    },
  };
}

export function speciesLabel(s: SpeciesRow | null | undefined): string {
  return speciesName(s);
}
